import { useCallback, useRef, useState } from "react"

import { useDateTime } from "../../../../localization/useDateTime"

import { type NarrowedAvailability, narrowToProductGroup } from "../productGroupAvailability"
import { type GroupItem, type ProductItem, type ProductTableRow, ProductTableRowType } from "../productsTypes"
import { useNarrowProductAvailability } from "./useNarrowProductAvailability"

type MoveProduct = () => Promise<unknown>

// a failed move is rolled back and a failed narrowing is shown by the mutations themselves
const ignoreApiError = () => undefined

export type PendingProductMove = Readonly<{
    product: ProductItem
    group: GroupItem
    narrowedAvailability: NarrowedAvailability
    move: MoveProduct
}>

export function useProductGroupAvailabilityMove(rows: ReadonlyArray<ProductTableRow>) {
    const dateTimeFactory = useDateTime()
    const narrowProductAvailability = useNarrowProductAvailability()
    const [pendingMove, setPendingMove] = useState<PendingProductMove | null>(null)

    // read at drop time, so the callbacks stay stable across rows updates
    const rowsRef = useRef(rows)
    rowsRef.current = rows

    const requestMove = useCallback(
        (product: ProductItem, targetGroupId: number | undefined, move: MoveProduct) => {
            const group =
                targetGroupId !== product.groupId
                    ? rowsRef.current.find(
                          (row): row is GroupItem => row.type === ProductTableRowType.GROUP && row.id === targetGroupId,
                      )
                    : undefined
            const narrowedAvailability =
                group &&
                narrowToProductGroup(product.availabilityDetails, group.groupAvailabilityDetails, dateTimeFactory)

            if (!group || !narrowedAvailability) {
                move().catch(ignoreApiError)
                return
            }

            setPendingMove({ product, group, narrowedAvailability, move })
        },
        [dateTimeFactory],
    )

    const confirmMove = useCallback(() => {
        if (!pendingMove) return

        const { product, narrowedAvailability, move } = pendingMove
        move()
            .then(() => narrowProductAvailability([{ productId: product.id, narrowedAvailability }]))
            .catch(ignoreApiError)
        setPendingMove(null)
    }, [pendingMove, narrowProductAvailability])

    const cancelMove = useCallback(() => setPendingMove(null), [])

    return { pendingMove, requestMove, confirmMove, cancelMove }
}
